import {openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {RentalOrderResponseBody, useRemoveRentalOrderMutation} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds} from '@dms/routes';

import {Nullish, useNavigate} from 'shared/web';

import {EntityHeaderAction} from '../components/EntityHeader/types/EntityHeaderAction';
import {RentalOrderCancelDialog} from '../components/RentalOrderCancelDialog/RentalOrderCancelDialog';
import {handleApiError} from '../utils/handleApiError';

export const useGetRentalOrderActions = (
  rentalOrder: RentalOrderResponseBody | Nullish
): EntityHeaderAction[] => {
  const navigate = useNavigate();

  const [removeRentalOrder, {isLoading: isRemovingRentalOrder}] = useRemoveRentalOrderMutation();

  if (isNil(rentalOrder)) {
    return [
      {
        type: 'button',
        variant: 'secondary',
        title: i18n.t('general.actions.discard'),
        onClick: () => navigate(rentalOrderRoutes.overview),
        'data-testid': testIds.rentalOrder.detail('discard'),
      },
    ];
  }

  const handleCancelOrder = () => {
    const dialogTestId = testIds.rentalOrder.detail('delete-order-dialog');

    openDialog(
      <RentalOrderCancelDialog
        data-testid={dialogTestId}
        rentalOrderId={rentalOrder.rentalOrderId}
      />,
      {
        'data-testid': dialogTestId,
        title: i18n.t('page.rentalOrder.labels.cancelOrder'),
        scrollBehavior: 'outside',
      }
    );
  };

  const handleRemoveOrder = () =>
    openDeleteDialog({
      onConfirm: () =>
        removeRentalOrder({rentalOrderId: rentalOrder.rentalOrderId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => navigate(rentalOrderRoutes.overview))
          .catch(handleApiError),
      text: i18n.t('page.rentalOrder.labels.deleteOrderDescription'),
      'data-testid': testIds.rentalOrder.detail('delete-order-dialog'),
    });

  return match<RentalOrderResponseBody['state'], EntityHeaderAction[]>(rentalOrder.state)
    .with(
      'draft',
      'canceled',
      always([
        {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('general.actions.delete'),
          onClick: handleRemoveOrder,
          isLoading: isRemovingRentalOrder,
          'data-testid': testIds.rentalOrder.detail('delete'),
        },
      ])
    )
    .with(
      'reserved',
      'returned',
      'closed',
      always([
        {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('page.rentalOrder.labels.cancelOrder'),
          onClick: handleCancelOrder,
          'data-testid': testIds.rentalOrder.detail('cancel'),
        },
      ])
    )
    .otherwise(always([]));
};
